import { ColumnMetadata, ColumnTypes } from "~/components/DataGrid";
import { Emdash } from "~/components/Punctuation";
import { AssetColumns } from "~/modules/assets/types";
import { AssetTableRowData } from "../types";
import { ImageLoader } from "~/components/ImageLoader";
import { getMeasureValue } from "~/utils/getMeasureValue";
import { cn } from "~/lib/utils";
import { Tooltip } from "~/components/Tooltip";
import { Link } from "~/components/Link";
import { Routes } from "~/routes";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkSquare } from "@fortawesome/pro-regular-svg-icons";
import {
    AggregateFunctions,
    AggregateQueryInterval,
    ApiQuery,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";
import {
    TokenTimeseriesColumns,
    TokenTimeseriesGroupBy,
} from "~/modules/token_timeseries/types";
import { MEASURE_SLUGS } from "~/consts";
import { AssetUserEvents } from "~/modules/analytics/events/assets";
import { env } from "~/env.mjs";
import { FeatureNames } from "~/modules/feature_flag/flags";

// // // //

// ENHANCEMENT - move this into src/utils?
// NOTE - this is really a counterpart to ApiSearchLayout - it should live in that module
function buildUrlWithApiQuery(props: {
    url: string;
    apiQuery?: ApiQuery;
}): string {
    const { url, apiQuery } = props;

    // Build URL to RWA_API_HOST w/ url
    const obj = new URL(url);

    // Build query parameters for request
    obj.searchParams.append("query", JSON.stringify(apiQuery));

    // Return the URL as a string
    return obj.toString();
}

export const BRIDGED_TOKEN_VALUE_DOLLAR_SPARKLINE_COLUMN: ColumnMetadata<
    AssetTableRowData,
    AssetColumns
> = {
    slug: "bridged_token_value_dollar_sparkline",
    label: "Last 30 Days",
    type: ColumnTypes.sparkline,
    width: 120,
    featureFlag: {
        feature: FeatureNames.enableExperimentalFeatures,
    },
    render: (item) => {
        if (!item.isAsset) {
            return <Emdash />;
        }

        const measure = getMeasureValue(item, "bridged_token_value_dollar");

        const positiveChange: boolean =
            measure?.chg_30d_pct && measure.chg_30d_pct > 0 ? true : false;

        const sparklineUrl = `https://sparklines.rwa.xyz/img/assets/${item.asset_id}/bridged_token_value_dollar/30d.svg`;

        const apiQuery: ApiQuery = {
            filter: {
                operator: FilterOperators.And,
                filters: [
                    {
                        field: TokenTimeseriesColumns.assetTicker,
                        operator: FilterOperators.Equals,
                        value: item.ticker,
                    },
                    {
                        field: TokenTimeseriesColumns.measureSlug,
                        operator: FilterOperators.Equals,
                        value: MEASURE_SLUGS.bridged_token_value_dollar,
                    },
                ],
            },
            sort: {
                direction: SortDirections.asc,
                field: TokenTimeseriesColumns.date,
            },
            pagination: {
                page: 1,
                perPage: 30,
            },
            aggregate: {
                groupBy: TokenTimeseriesGroupBy.measure,
                aggregateFunction: AggregateFunctions.Sum,
                interval: AggregateQueryInterval.day,
            },
        };

        const explorerUrl = buildUrlWithApiQuery({
            url: `${env.NEXT_PUBLIC_BASE_URL}${Routes.tokenTimeseries}`,
            apiQuery,
        });

        return (
            <Tooltip
                placement="top"
                tooltipContent={
                    <div className="px-3 py-2 text-sm bg-foreground text-background rounded-md shadow-md max-w-xs flex items-center gap-1">
                        Open in Time Series Explorer
                        <FontAwesomeIcon
                            icon={faExternalLinkSquare}
                            className="w-4 h-4"
                        />
                    </div>
                }
            >
                {({ setTriggerRef }) => (
                    <Link
                        openInNewTab
                        href={explorerUrl}
                        setTriggerRef={setTriggerRef}
                        analyticsLabel={AssetUserEvents.ClickSparklineChart}
                        analyticsEventProperties={{
                            asset: item.ticker,
                            measure: "bridged_token_value_dollar",
                        }}
                    >
                        <ImageLoader
                            src={sparklineUrl}
                            alt={""}
                            className={cn("w-full", {
                                "hue-rotate-85 saturate-80 brightness-[0.85]":
                                    positiveChange,
                                "hue-rotate-300 saturate-210 brightness-[0.7] contrast-170":
                                    !positiveChange,
                            })}
                        />
                    </Link>
                )}
            </Tooltip>
        );
    },
};
